import { Account, cn } from '@liquidbudget/common';
import { AlertTriangleIcon, AsteriskIcon, Unplug } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { AccountMenu } from '@/components/budget-nav/account-menu';

type AccountNavLinkProps = {
  account: Account;
  to: string;
  text: string;
  indicator?: boolean;
  aside?: string;
  error?: boolean;
  warning?: boolean;
};

export function AccountNavLink({
  to,
  text,
  aside,
  account,
  indicator,
  error = false,
  warning = false,
}: AccountNavLinkProps) {
  const { pathname } = useLocation();
  const isActive = pathname === to;

  const linkStyles = getLinkStyles(isActive, error, warning);
  const icon = getStatusIcon(error, warning, indicator);

  return (
    <div className="group/item flex items-center space-x-1 w-full">
      <AccountMenu account={account} />

      <Link to={to} className={cn('flex items-center w-full py-2 pl-2 pr-4 rounded-md transition-colors', linkStyles)}>
        <span>{text}</span>
        <span className="flex-grow -ml-1">{icon}</span>
        {aside && <span className="text-xs tabular-nums">{aside}</span>}
      </Link>
    </div>
  );
}

/**
 * Determines the appropriate link styles based on state.
 * Error styles take precedence over warning styles.
 *
 * @param isActive - Whether the link is currently active
 * @param error - Whether the account has an error state
 * @param warning - Whether the account has a warning state
 * @returns The appropriate CSS classes for the link
 */
function getLinkStyles(isActive: boolean, error: boolean, warning: boolean): string {
  if (error) {
    if (isActive) {
      return 'bg-red-200 text-red-900 border border-red-400 dark:bg-red-900/60 dark:text-red-100 dark:border-red-700';
    }
    return 'bg-red-100 text-red-800 border border-red-300 shadow-sm hover:bg-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800/60 dark:hover:bg-red-900/60';
  }

  if (warning) {
    if (isActive) {
      return 'bg-amber-200 text-amber-900 border border-amber-400 dark:bg-amber-900/60 dark:text-amber-100 dark:border-amber-700';
    }
    return 'bg-amber-100 text-amber-800 border border-amber-300 shadow-sm hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800/60 dark:hover:bg-amber-900/60';
  }

  if (isActive) {
    return 'bg-primary text-primary-foreground';
  }

  return 'hover:bg-accent hover:text-accent-foreground';
}

/**
 * Returns the appropriate status icon based on priority.
 * Priority order: error > warning > indicator
 *
 * @param error - Whether the account has an error state
 * @param warning - Whether the account has a warning state
 * @param indicator - Whether to show a general indicator
 * @returns The appropriate icon element or null
 */
function getStatusIcon(error: boolean, warning: boolean, indicator?: boolean): React.ReactNode | null {
  if (error) {
    return <AlertTriangleIcon className="ml-2 h-3 w-3" />;
  }

  if (warning) {
    return <Unplug className="ml-2 h-3 w-3" />;
  }

  if (indicator) {
    return <AsteriskIcon className="ml-2 h-3 w-3" />;
  }

  return null;
}
